import { useMemo, useState, useEffect } from "react";
import { datadogRum } from "@datadog/browser-rum";
import { Form, Formik, FormikErrors, FormikProps, FormikTouched } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from "react-router-dom";
import { getRedirectUrlWithoutHostname } from "@iddataweb/idw-common-ui/utils";
import { AttributeStatusTypes } from "@enums/attribute-status-types";
import { VerifiedAttribute } from "@idw-types/data/verified-attribute";
import { VerificationResponse } from "@idw-types/requests/verification-response";
import { AttributeFormState } from "@idw-types/state/attribute-form-state";
import {
    disableAsyncButton,
    disableSubmitButton,
    setAssertionErrorMessages,
    setAsyncModalMessage,
    setRetryVerification,
    setShowAsyncModal,
    updateAttempt,
    updateAttributeVerifiedStatuses,
} from "@redux/actions/verification-form-actions";
import {
    resetSubmissionSubmitStatus,
    setSubmissionComplete,
    setSubmissionProcessing,
    setSubmissionSubmitting
} from "@redux/actions/submission-actions";
import {
    getAsi,
    getAutoSubmitPrefilledData,
    getDCUIPreverify,
    getIsSuccessPageEnabled,
    getOidcCallbackURL,
    getRedirectToCallbackUrlOnError,
    getSyncDCUIRetryURL,
    getTMXAutoSubmit,
    getVerifyToken,
    getVerifyURL
} from "@redux/selectors/verification-configuration-selectors";
import {
    getAllowDCUIretry,
    getAsyncCompleted,
    getAttributeData,
    getPreviouslySavedAttributeData
} from "@redux/selectors/verification-form-selectors";
import {
    getActiveLanguage,
    getLanguageState
} from "@redux/selectors/language-selectors";
import { getContextPath } from "@redux/selectors/query-parameters-selectors";
import { setError } from "@redux/actions/error-actions";
import {
    setLandingPageData,
    setRedirectUrl,
    setShouldRedirect,
    setShouldUpdateSubmitButton
} from "@redux/actions/common-actions";
import { setVerifyToken } from "@redux/actions/verification-configuration-actions";
import { getSubmissionState } from "@redux/selectors/submission-selectors";
import {
    submitVerificationData,
    getVerifyRequestData
} from "@requests/submit-verification-data";
import { startAsyncDataCapture } from "@requests/start-async-data-capture";
import { getAttributeFormInitialValue } from "@utils/attribute-data-util";
import { Logger } from '@utils/logger';
import { navigateToUtilWithQueryParameters, handleNavigationForError } from "@utils/navigation-util";
import { AttributeListComponent } from "./attribute-list-component";
import { TMXContainer } from "../tmx-container/tmx-container";
import { LoadingSpinner } from "../../common/loading-spinner/loading-spinner";
import { PostAttributeListContainer } from "../post-attribute-list-container/post-attribute-list-container";
import { generateAttributeValidationSchema } from "../../../utils/attribute-validation-util";
// import { setVerificationMessage } from "@redux/actions/common-actions";

type AttributeListContainerProps = {
    useThemedUI: boolean;
    canShowPolicyDecisionRetryMessage: boolean;
    setCanShowPolicyDecisionRetryMessage: (canShow: boolean) => void;
};

export const AttributeListContainer = (props: AttributeListContainerProps) => {
    const {
        useThemedUI,
        canShowPolicyDecisionRetryMessage,
        setCanShowPolicyDecisionRetryMessage
    } = props;

    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();

    const [shouldUpdateInitialValues, setShouldUpdateInitialValues] = useState(true);
    const [initialValues, setInitialValues] = useState({});
    const [hasFinishedTMXProfiling, setHasFinishedTMXProfiling] = useState(false);

    const autoSubmitPrefilledData = useSelector(getAutoSubmitPrefilledData);
    const tmxAutoSubmit = useSelector(getTMXAutoSubmit);
    const asyncCompleted = useSelector(getAsyncCompleted);
    const languageState = useSelector(getLanguageState);
    const asi = useSelector(getAsi);
    const attributeData = useSelector(getAttributeData);
    const previouslySavedAttributeData = useSelector(getPreviouslySavedAttributeData);
    const dcuiPreverify = useSelector(getDCUIPreverify);
    const syncDCUIRetryURL = useSelector(getSyncDCUIRetryURL);
    const contextPath = useSelector(getContextPath);
    const verifyToken = useSelector(getVerifyToken);
    const verifyURL = useSelector(getVerifyURL);
    const allowDCUIretry = useSelector(getAllowDCUIretry);
    const isSuccessPageEnabled = useSelector(getIsSuccessPageEnabled);
    const submissionState = useSelector(getSubmissionState);
    const redirectToCallbackUrlOnError = useSelector(getRedirectToCallbackUrlOnError);
    const oidcCallbackURL = useSelector(getOidcCallbackURL);
    const activeLanguage = useSelector(getActiveLanguage);
    // const [requestCounter, setRequestCounter] = useState(0);

    const handleVerificationResponse = (verificationResponse: VerificationResponse, setIsSubmitting: (isSubmitting: boolean) => void) => {
        Logger.info('JSON to verify', verificationResponse);

        const { verifyToken, verifyResponseData } = verificationResponse;
        const {
            attempt,
            message,
            alreadyRPAssignedFlag,
            verificationDoneFlag,
            uiAttributeList,
            assertionMessages,
            idToken,
            forwardURL,
            resultsDisplayType
        } = verifyResponseData;

        Logger.info('Verification response, attempt ' + attempt);

        dispatch(setVerifyToken(verifyToken));

        if (message) {
            Logger.info(message);
        }

        // handle flags
        // user has already assigned data to this RP, immediately forward to appropriate url
        if (alreadyRPAssignedFlag || verificationDoneFlag) {
            // Disable the Formik form so the user can not interact with the content
            setIsSubmitting(true);
            // Disable navigation warning message so redirect can occur
            dispatch(setShouldRedirect(true));

            if (idToken && resultsDisplayType) {
                dispatch(setSubmissionProcessing(false));
                Logger.info(`New mockrp triggered - redirecting internally`);
                const trimmedRedirectUrl = forwardURL.split("?")[0];
                const redirectUrlWithoutHostname = getRedirectUrlWithoutHostname(trimmedRedirectUrl);
                dispatch(setRedirectUrl(redirectUrlWithoutHostname));
                dispatch(setLandingPageData(idToken));
                navigateToUtilWithQueryParameters(`/${redirectUrlWithoutHostname}`, navigate, location.pathname, location.search);
            } else {
                Logger.info(`Redirecting triggered, ${forwardURL}`);
                dispatch(setRedirectUrl(forwardURL));
                dispatch(setRetryVerification(false));

                if (isSuccessPageEnabled) {
                    navigateToUtilWithQueryParameters("/success", navigate, location.pathname, location.search);
                } else {
                    setTimeout(() => {
                        window.location.href = forwardURL;
                    }, 100);
                }
            }

            return;
        }

        dispatch(updateAttempt(attempt));

        if (uiAttributeList) {
            const verifiedAttributes: Array<VerifiedAttribute> = [];
            // handle the responses.
            uiAttributeList.forEach((verifiedAttribute: VerifiedAttribute) => {
                const currentAttribute = attributeData.find(afs => afs.id === verifiedAttribute.id);
                if (currentAttribute) {
                    Logger.info(`Parsing response for attribute ${currentAttribute.type}, id ${verifiedAttribute.id} :  ${verifiedAttribute.status}`);
                    Logger.info(`oobStatus: ${verifiedAttribute.status}`);
                    verifiedAttributes.push(verifiedAttribute);
                } else {
                    throw new Error(`Attribute ${verifiedAttribute.id} does not match an existing attribute`);
                }
            });
            dispatch(updateAttributeVerifiedStatuses(verifiedAttributes, attempt, allowDCUIretry));
        }

        if (assertionMessages && assertionMessages.length) {
            dispatch(setAssertionErrorMessages(assertionMessages));
        } else {
            dispatch(setAssertionErrorMessages([]));
        }

        if (uiAttributeList) {
            // Check if we need to retry verification
            const shouldRetryVerification = uiAttributeList.some(
                (verifiedAttribute: AttributeFormState) =>
                    verifiedAttribute.status === AttributeStatusTypes.FAIL ||
                    verifiedAttribute.status === AttributeStatusTypes.NEW ||
                    verifiedAttribute.status === AttributeStatusTypes.PICKER
            );
            dispatch(setRetryVerification(shouldRetryVerification));

        } else {
            dispatch(setRetryVerification(true));
        }

        dispatch(setSubmissionComplete(true));
        dispatch(setShouldUpdateSubmitButton(true));
    };

    const handleSubmit = async (values: any, { setSubmitting }: { setSubmitting: (isSubmitting: boolean) => void }) => {
        setCanShowPolicyDecisionRetryMessage(false);

        //set form status to submitting
        setSubmitting(true);
        dispatch(setSubmissionSubmitting(true));

        //clear out previous errors and submit status
        dispatch(setAssertionErrorMessages([]));
        dispatch(resetSubmissionSubmitStatus());

        if (asyncCompleted || tmxAutoSubmit || autoSubmitPrefilledData) {
            dispatch(disableSubmitButton(true));
            dispatch(setSubmissionProcessing(true));

            const verifyRequestData = getVerifyRequestData(attributeData, verifyToken, asi, languageState.activeLanguage);

            try {
                const response = await submitVerificationData(verifyRequestData, verifyURL);

                const jsonResponse: VerificationResponse = await response.json();

                if (response.ok) {
                    handleVerificationResponse(jsonResponse, setSubmitting);
                } else {
                    const error = {
                        asi: asi,
                        errorCode: jsonResponse.verifyResponseData?.errorCode || response.statusText,
                        message: jsonResponse.verifyResponseData?.errorMessage || "Error processing submission",
                        errorURI: response.url,
                        status: `${response.status}`,
                        forwardedErrorCode: jsonResponse.verifyResponseData?.errorCode,
                        forwardedErrorDescription: jsonResponse.verifyResponseData?.errorMessage
                    };
                    throw error;
                }
            } catch (error) {
                dispatch(setError({
                    name: "Submit Verification Data Error",
                    asi: asi,
                    errorCode: error.errorCode,
                    errorURI: error.errorURI,
                    httpStatus: error.status,
                    language: verifyRequestData.language,
                    message: error.message
                }));
                Logger.error('An error occurred.', error);
                datadogRum.addError(error);
                handleNavigationForError(dispatch)(
                    redirectToCallbackUrlOnError,
                    oidcCallbackURL,
                    asi,
                    "/error",
                    navigate,
                    location.pathname,
                    location.search,
                    error
                );
                return;
            }

            setSubmitting(false);
            setSubmissionSubmitting(false);
        } else {
            //disable the async button
            dispatch(disableAsyncButton(true));
            dispatch(setSubmissionProcessing(true));

            try {
                const response = await startAsyncDataCapture(dispatch, navigate, location)(
                    dcuiPreverify,
                    syncDCUIRetryURL,
                    asi,
                    attributeData,
                    contextPath,
                    setSubmitting,
                    redirectToCallbackUrlOnError,
                    oidcCallbackURL,
                    activeLanguage
                );
                Logger.info('Got successful response from start Async');
                //if success
                if (response.status === 'success') {
                    //show the modal
                    dispatch(setShowAsyncModal(true));
                    dispatch(setAsyncModalMessage('common.asyncModal.waiting'));
                } else {
                    Logger.error('There was a problem sending the sms message: ' + response.errorMessage);
                    const error = new Error('There was a problem sending the sms message: ' + response.errorMessage);
                    datadogRum.addError(error);
                }
            } catch (error) {
                console.error(error);
            }
        }

        return;
    };

    const waitBeforeRendering = useMemo(() => {
        return (!attributeData || !languageState.initialized);
    }, [
        attributeData,
        languageState.initialized,
    ]);

    useEffect(() => {
        if (attributeData && shouldUpdateInitialValues) {
            setShouldUpdateInitialValues(false);

            const newInitialValues = attributeData.map((attributeFormState) => {
                return getAttributeFormInitialValue(
                    attributeFormState,
                    previouslySavedAttributeData.find(afs => afs.id === attributeFormState.id) || null
                );
            })
            setInitialValues(newInitialValues);
        }
    }, [shouldUpdateInitialValues, attributeData]);

    const validationSchema = useMemo(() => {
        return generateAttributeValidationSchema(attributeData);
    }, [attributeData]);

    //TODO - needs to be translated
    if (waitBeforeRendering) {
        return <LoadingSpinner loadingText="Working..." />;
    }

    const renderForm = (
        errors: FormikErrors<any>,
        touched: FormikTouched<any>,
        setFieldTouched: (field: string, isTouched?: boolean, shouldValidate?: boolean) => void,
        setFieldValue: (field: string, value: any, shouldValidate?: boolean) => Promise<void> | Promise<FormikErrors<any>>,
        isValid: boolean,
        isSubmitting: boolean,
        submitForm: (() => Promise<void>) & (() => Promise<any>)
    ) => {
        return (
            <Form className={`needs-validation`} noValidate>
                <TMXContainer setHasFinishedTMXProfiling={setHasFinishedTMXProfiling} submitForm={submitForm} />
                <AttributeListComponent
                    submitForm={submitForm}
                    errors={errors}
                    touched={touched}
                    isSubmitting={isSubmitting || submissionState.processing}
                    setFieldTouched={setFieldTouched}
                    setFieldValue={setFieldValue}
                    attributeFormStates={attributeData}
                />
                <PostAttributeListContainer
                    canShowPolicyDecisionRetryMessage={canShowPolicyDecisionRetryMessage}
                    hasFinishedTMXProfiling={hasFinishedTMXProfiling}
                    isValid={isValid}
                    errors={errors}
                    isSubmitting={isSubmitting}
                    useThemedUI={useThemedUI}
                />
            </Form>
        );
    };

    return (
        <Formik
            initialValues={initialValues}
            validateOnMount={true}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
        >
            {({ errors, touched, setFieldTouched, setFieldValue, isValid, isSubmitting, submitForm }: FormikProps<any>) => (
                renderForm(
                    errors,
                    touched,
                    setFieldTouched,
                    setFieldValue,
                    isValid,
                    isSubmitting,
                    submitForm
                )
            )}
        </Formik>
    );
};